import { defineMessage } from 'react-intl'

import axios from 'axios'
import { fromJS, Map } from 'immutable'
import { AnyAction, Dispatch } from 'redux'

import {
  AppointmentClass,
  AvailabilitiesResponse,
  BILLABLE_TYPE,
  BillableType,
  CALENDAR_SERVICE_BASE_URL,
  DAILY_END_TIME,
  DAILY_START_TIME,
  makeProvidersListKeywordTrackingData,
  ProviderProgramTaxonomy,
  S4C_PROPERTIES,
  SLICE_DURATION_USER_MINUTES,
  TREATMENT_OPTIONS_TYPE,
} from '@lyrahealth-inc/shared-app-logic'

import * as BannerActions from '../../../../common/components/banner/banner-actions'
import { getJwt } from '../../../../common/http/data/jwt'
import { getAllTreatmentOptionsCosts, getProvider, getTreatmentTypeCost } from '../../../../common/http/data/onboard'
import { logToSumoLogic } from '../../../../common/utils/userUtils'
import { getShowProviderAdjectives } from '../../../../data/featureFlags/featureFlagSelectors'
import { trackOnboardEvent } from '../../../../data/mixpanel'
import { getSelectedSearchId } from '../../../triage/data/triageSelectors'
import { createCrmNotificationForProviderSelection } from '../../data/crmActions'
import { getOnboardProvidersDisplayed, getOnboardUserFavoriteProviderRequestId } from '../../data/onboardSelectors'

export const GET_PROVIDER_COST_DATA = 'GET_PROVIDER_COST_DATA'
export const SET_PROVIDER_PROFILE_DATA = 'SET_PROVIDER_PROFILE_DATA'
export const CLEAR_PROVIDER_PROFILE_DATA = 'CLEAR_PROVIDER_PROFILE_DATA'
export const SET_PROVIDER_AVAILABILITY_DATA_CS = 'SET_PROVIDER_AVAILABILITY_DATA_CS'

export const getProviderFromStore = (id: string) => {
  return (dispatch: Dispatch<AnyAction>, getState: () => Map<string, any>) => {
    const foundProvider = getOnboardProvidersDisplayed(getState()).find(($$provider) => {
      return $$provider && $$provider.lyra_id === id
    })
    if (foundProvider && (foundProvider as $TSFixMe).size !== 0) {
      Promise.resolve(foundProvider)
      return dispatch(setProviderProfileData(foundProvider))
    } else {
      return Promise.reject(new Error('No provider found'))
    }
  }
}

export const getProviderProfileData = (id: $TSFixMe) => {
  const request = getProvider(id)

  return (dispatch: $TSFixMe) => {
    return new Promise(function (resolve, reject) {
      request.then(
        (providerReturn) => {
          dispatch(setProviderProfileData(fromJS(providerReturn).getIn(['data', '0', 'providersInfo', '0'])))
          resolve(providerReturn)
        },
        (error) => {
          const errorMessageProps = BannerActions.getApiErrorMessageProps(error)
          dispatch(
            BannerActions.setBannerPropsAndStatus({
              bannerType: BannerActions.BANNER_TYPE.DANGER,
              message: defineMessage({
                defaultMessage: 'Unable to get provider profile data',
                description: "Error message when getting provider's profile fails",
              }),
              errorMessageProps,
            }),
          )
          reject(error)
        },
      )
    })
  }
}

export const setProviderProfileData = (data: $TSFixMe) => {
  return {
    type: SET_PROVIDER_PROFILE_DATA,
    payload: data,
  }
}

export const clearProviderProfileData = () => {
  return {
    type: CLEAR_PROVIDER_PROFILE_DATA,
  }
}

// Given a provider, sets said provider as the chosen provider and navigates directly to the provider's profile.
// This should only be used when the provider is the only provider returned, but it is possible to shoehorn it in
// with a different use, so the Mixpanel event signaling this is conditional.
export const setProvider = ({ provider, isOnlyProviderReturned = false, mixpanelPayload = {} }: $TSFixMe) => {
  return (dispatch: $TSFixMe, getState: () => Map<string, any>) => {
    const $$state = getState()
    const showProviderAdjectives = getShowProviderAdjectives($$state)

    dispatch(setProviderProfileData(provider))
    if (isOnlyProviderReturned) {
      dispatch(
        trackOnboardEvent({
          event: 'View provider recommendations - single',
          ...(showProviderAdjectives
            ? {
                [S4C_PROPERTIES.ADJECTIVES_AND_KEYWORDS_SHOWN]: makeProvidersListKeywordTrackingData({
                  providers: [provider],
                  showProviderAdjectives,
                }),
              }
            : {}),

          ...mixpanelPayload,
        }),
      )
    }
    dispatch(createCrmNotificationForProviderSelection({ provider }))
  }
}

export interface GetProviderInitialSessionCostParams {
  providerId: string
  userId: string
  outsideOnboarding: boolean
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE
  blendedCareProgramId?: string | null
  sessionType?: string | null
  childLyraId?: string | null
  shouldShowSelfPayFlow?: boolean
}

/**
 * Please use useFetchProviderSessionCostAndCalendar instead of this action directly
 */
export const getProviderInitialSessionCost = ({
  providerId,
  userId,
  outsideOnboarding,
  selectedTreatmentOption,
  blendedCareProgramId,
  sessionType,
  childLyraId,
  shouldShowSelfPayFlow,
}: GetProviderInitialSessionCostParams) => {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const state = getState()
    const searchId = getSelectedSearchId(state)
    const requestId = getOnboardUserFavoriteProviderRequestId(state)

    // PROSPECT-3699 - Protects against the case where a user navigates backwards to an onboard provider profile link after exiting triage.
    // At this point we can't assume the searchId that was associated with the onboard provider profile, which is used to determine the treatment type
    // so we treat it the same as visiting a direct link provider profile when estimating the cost.
    const treatmentOptionTypeUnknown = outsideOnboarding || !searchId
    const billableType: BillableType | undefined = shouldShowSelfPayFlow ? BILLABLE_TYPE.SELFPAY : undefined
    const request = treatmentOptionTypeUnknown
      ? getAllTreatmentOptionsCosts({ providerId, userId, sessionType, childLyraId, billableType })
      : getTreatmentTypeCost(providerId, userId, searchId, requestId, billableType)

    return new Promise(function (resolve, reject) {
      request.then(
        (costReturn) => {
          let costInfo

          if (treatmentOptionTypeUnknown && costReturn?.data?.length === 1) {
            /*
              If the user lands on a provider's profile via direct link, the BE will return an array of costs
              associated with each treatment type offered. If the provider offers only one treatment option, we
              display the cost of that treatment option, else costInfo defaults to an empty object, resulting in a
              hardcoded cost range displayed
            */
            costInfo = costReturn?.data[0]
          } else {
            // The user is in onboarding so the BE will return the cost of the user's selected treatment option
            costInfo = costReturn?.data
          }

          dispatch({
            type: GET_PROVIDER_COST_DATA,
            payload: costInfo || {},
          })
          resolve(costInfo)
        },
        (error) => {
          dispatch({
            type: GET_PROVIDER_COST_DATA,
            payload: {},
          })
          const errorMessage = 'Unable to get provider initial session cost. ' + error.message
          logToSumoLogic('lyraWebErrors', userId, {
            message: errorMessage,
            outsideOnboarding,
            selectedTreatmentOption,
            blendedCareProgramId,
          })
          reject(error)
        },
      )
    })
  }
}

/**
 * Please use useFetchProviderSessionCostAndCalendar instead of this action directly
 */
export const reconcileProviderCalendarService = ({ providerId }: { providerId: string }) => {
  return (dispatch: Dispatch<AnyAction>, getState: () => Map<string, any>) => {
    return new Promise<AvailabilitiesResponse | void>(function (resolve, reject) {
      if (!getState().getIn(['appGlobals', 'isHydrated'])) {
        dispatch({
          type: 'RECONCILE_PROVIDER_FAILED',
          error: 'Store was not hydrated. Try again once the store is hydrated to prevent race conditions.',
        })
        return reject(new Error('Error reconciling Provider because the store was not hydrated'))
      }

      return dispatch(getProviderFromStore(providerId) as any)
        .then(undefined, () => {
          // The provider isn't in the store, so retrieve their data via API
          resolve(dispatch(getProviderProfileData(providerId) as any))
        })
        .catch((error: any) => reject(error))
    })
  }
}

type GetProviderAvailabilityCalendarServiceParams = {
  programTaxonomy: Omit<ProviderProgramTaxonomy, 'role'>
  appointmentClass: AppointmentClass
  numDaysToFetchMs: number
  appointmentDuration?: number
}
export const getProviderAvailabilityCalendarService = (
  providerId: string,
  params: GetProviderAvailabilityCalendarServiceParams,
) => {
  return (dispatch: Dispatch<AnyAction>, _: () => Map<string, any>) => {
    return new Promise<AvailabilitiesResponse | void>(function (resolve) {
      getProviderAvailabilityCalendarServicePromise([providerId], params).then((response) => {
        dispatch(setProviderAvailabilityCalendarService(response))
        resolve(response)
      })
    })
  }
}

export const getProviderAvailabilityCalendarServicePromise = async (
  providerIds: string[],
  params: GetProviderAvailabilityCalendarServiceParams,
): Promise<AvailabilitiesResponse> => {
  const { data: token } = await getJwt()
  const { data: availability } = await axios.post(
    `${process.env.CS_API_URL ?? CALENDAR_SERVICE_BASE_URL}/api/v1/provider/availability`,
    { provider_ids: providerIds },
    {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        slice_duration_minutes: SLICE_DURATION_USER_MINUTES,
        appointment_duration_minutes: params?.appointmentDuration,
        start_datetime: new Date().toISOString(),
        end_datetime: new Date(Date.now() + params.numDaysToFetchMs).toISOString(),
        daily_start_time: DAILY_START_TIME,
        daily_end_time: DAILY_END_TIME,
        clientele: params.programTaxonomy.clientele,
        treatment: params.programTaxonomy.treatment,
        partner: params.programTaxonomy.partner,
        offering: params.programTaxonomy.offering,
        appointment_class: params.appointmentClass,
      },
    },
  )
  return availability
}

export const setProviderAvailabilityCalendarService = (data: AvailabilitiesResponse) => {
  return {
    type: SET_PROVIDER_AVAILABILITY_DATA_CS,
    payload: data,
  }
}
